'use client';

import { Skeleton } from '@/components/ui/app-skeleton';
import { Panel } from '@/components/ui/panel';
import { cn } from '@/lib/ui/utils';

/**
 * Loading placeholder for {@link MarketRowCard}.
 *
 * Zero-CLS by construction: the card is height-locked from md up by its
 * `md:aspect-[4/3]` ratio, so this skeleton only has to share that lock and
 * the same outer `Panel` chrome — the interior heights are irrelevant to the
 * card's total height, which is fixed by the aspect box. That means the
 * skeleton can't drift on the card's (1998-LOC) interior the way a
 * height-mirroring skeleton does; the bar region just fills the locked box.
 */
export function MarketRowCardSkeleton(): React.ReactNode {
  return (
    <Panel
      surface="solid"
      elevation="flat"
      className="relative flex h-full min-h-0 w-full min-w-0 flex-col gap-1 overflow-hidden p-2 md:aspect-[4/3]"
      data-testid="market-row-card-skeleton"
    >
      {/* Cover + title row — mirrors MarketRowCard's title row. */}
      <div className="flex shrink-0 items-center gap-3 p-1">
        <div className="relative size-12 shrink-0">
          <Skeleton className="size-12 rounded-control" />
          <Skeleton className="absolute -right-1 -bottom-1 size-5 rounded-full" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Skeleton className="h-5 w-full rounded-item" />
          <Skeleton className="h-5 w-3/5 rounded-item" />
        </div>
      </div>

      {/* Bar region — fills the aspect box (flex-1 + md:min-h-0), same as the
          live card. Three vertical bars over a row of three outcome pills. */}
      <div className={cn('flex min-h-36 flex-1 flex-col gap-2 px-1 pt-2', 'md:min-h-0')}>
        <div className="flex min-h-0 w-full flex-1 gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="min-h-0 min-w-0 flex-1 rounded-control" />
          ))}
        </div>
        <div className="flex w-full shrink-0 gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-8 min-w-0 flex-1 rounded-control" />
          ))}
        </div>
      </div>

      {/* Footer — Resolves · traded · pool + bookmark slot. */}
      <div className="flex shrink-0 items-center gap-2 px-1 pb-1">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Skeleton className="h-3 min-w-0 flex-1 rounded-item" />
          <Skeleton className="h-3 min-w-0 flex-1 rounded-item" />
          <Skeleton className="h-3 min-w-0 flex-1 rounded-item" />
        </div>
        <Skeleton className="size-4 shrink-0 rounded-item" />
      </div>
    </Panel>
  );
}
